import { transform } from 'sucrase';

/**
 * Validate code by attempting to transpile it
 */
export const validateCode = (code, filename) => {
    try {
        let cleanCode = code;
        
        cleanCode = cleanCode.replace(/import\s+[\s\S]*?from\s+['"][^'"]+['"];?\s*/g, '');
        cleanCode = cleanCode.replace(/import\s+['"][^'"]+['"];?\s*/g, '');
        cleanCode = cleanCode.replace(/export\s+default\s+function\s+(\w+)/g, 'var $1 = function $1');
        cleanCode = cleanCode.replace(/export\s+default\s+class\s+(\w+)/g, 'var $1 = class $1');
        cleanCode = cleanCode.replace(/export\s+default\s+(\w+)\s*;?\s*$/gm, '');
        cleanCode = cleanCode.replace(/export\s+(const|let|var|function|class)\s+/g, '$1 ');
        cleanCode = cleanCode.replace(/^(const|let)\s+/gm, 'var ');
        
        transform(cleanCode, {
            transforms: ['jsx'],
            jsxRuntime: 'classic',
            production: true
        });
        
        return { valid: true, error: null };
    } catch (err) {
        return { valid: false, error: err.message };
    }
};

/**
 * Try to fix common code issues
 */
export const tryFixCode = (code) => {
    let fixed = code;
    
    // 1. Remove trailing non-code content
    const textIndicators = [
        '\n\nAceastă', '\n\nAceasta', '\n\nAplicația', '\n\nCodul',
        '\n\n**Funcționalități', '\n\n## ', '\n\n### ', '\n\n---',
        '\n\nNotă:', '\n\nNote:', '\n\n**Note', '\nÎn acest cod',
        '\nPrincipale', '\nFuncționalități:'
    ];
    
    for (const indicator of textIndicators) {
        const idx = fixed.indexOf(indicator);
        if (idx !== -1 && idx > 500) {
            fixed = fixed.substring(0, idx);
        }
    }
    
    // 2. Clean trailing incomplete lines
    const lines = fixed.split('\n');
    while (lines.length > 0) {
        const lastLine = lines[lines.length - 1].trim();
        if (
            lastLine === '' ||
            lastLine.endsWith(';') ||
            lastLine.endsWith('}') ||
            lastLine.endsWith(')') ||
            lastLine === 'export default App;' ||
            lastLine === 'export default App'
        ) break;
        lines.pop();
    }
    fixed = lines.join('\n');
    
    // 3. Ensure export exists
    if (!fixed.includes('export default') && fixed.includes('const App')) {
        fixed = fixed.trim() + '\n\nexport default App;';
    }
    
    // 4. Balance brackets
    const openBraces = (fixed.match(/\{/g) || []).length;
    const closeBraces = (fixed.match(/\}/g) || []).length;
    
    if (openBraces > closeBraces) {
        fixed = fixed + '\n' + '}'.repeat(openBraces - closeBraces);
    }
    
    return fixed;
};

/**
 * Extract code files from AI response
 */
export const extractCodeFromResponse = (response) => {
    const files = {};
    
    console.log('=== EXTRACTING CODE ===');
    console.log('Response length:', response.length);
    
    // Method 1: Match ```filename:App.jsx format (preferred)
    const filenameBlockRegex = /```filename:([^\n]+)\n([\s\S]*?)```/g;
    let match;
    
    while ((match = filenameBlockRegex.exec(response)) !== null) {
        let [, filename, code] = match;
        filename = filename.trim();
        if (filename && filename.includes('.')) {
            files[filename] = code.trim();
            console.log('Method 1 - Extracted:', filename, 'length:', code.length);
        }
    }
    
    // Method 2: Match ```App.jsx or ```path/file.ext format
    if (Object.keys(files).length === 0) {
        const simpleFilenameRegex = /```([A-Za-z0-9_\-\/]+\.[a-z]{2,4})\n([\s\S]*?)```/g;
        while ((match = simpleFilenameRegex.exec(response)) !== null) {
            let [, filename, code] = match;
            filename = filename.trim();
            if (filename && !['jsx', 'tsx', 'js', 'ts', 'css', 'html'].includes(filename.toLowerCase())) {
                files[filename] = code.trim();
                console.log('Method 2 - Extracted:', filename, 'length:', code.length);
            }
        }
    }
    
    // Method 2.5: Match // filename: App.jsx or /* App.jsx */ at start of code block
    if (Object.keys(files).length === 0) {
        const commentFilenameRegex = /```(?:jsx|javascript|js|tsx|ts)?\n(?:\/\/\s*(?:filename:?\s*)?([^\n]+\.[jt]sx?)\n|\/\*\s*([^\n]+\.[jt]sx?)\s*\*\/\n)([\s\S]*?)```/g;
        while ((match = commentFilenameRegex.exec(response)) !== null) {
            let filename = (match[1] || match[2] || '').trim();
            let code = match[3].trim();
            if (filename && code.length > 50) {
                files[filename] = code;
                console.log('Method 2.5 - Extracted from comment:', filename, 'length:', code.length);
            }
        }
    }
    
    // Method 3: Match ```jsx or ```javascript blocks containing App
    if (Object.keys(files).length === 0) {
        const jsxBlockRegex = /```(?:jsx|javascript|react|js)\n([\s\S]*?)```/g;
        let jsxMatch;
        while ((jsxMatch = jsxBlockRegex.exec(response)) !== null) {
            const code = jsxMatch[1].trim();
            // Check if this looks like a complete React component
            const hasComponent = code.includes('const App') || code.includes('function App') || 
                                 code.includes('export default') || code.includes('export function');
            const hasJSX = code.includes('return') && (code.includes('<div') || code.includes('<main') || 
                          code.includes('<section') || code.includes('<header') || code.includes('<'));
            
            if (hasComponent && hasJSX && code.length > 100) {
                // Try to determine filename from context
                let filename = 'App.jsx';
                
                // Check for other component patterns
                const componentMatch = code.match(/(?:export\s+(?:default\s+)?)?(?:const|function)\s+(\w+)\s*[=(:]/);
                if (componentMatch && componentMatch[1] !== 'App') {
                    filename = `${componentMatch[1]}.jsx`;
                }
                
                files[filename] = code;
                console.log('Method 3 - Extracted:', filename, 'length:', code.length);
                break;
            }
        }
    }
    
    // Method 3.5: Look for filename mentioned before code block
    if (Object.keys(files).length === 0) {
        // Pattern: **App.jsx:** or `App.jsx`: or App.jsx: followed by code block
        const namedBlockRegex = /(?:\*\*|`)?([A-Za-z0-9_\-\/]+\.[jt]sx?)(?:\*\*|`)?:?\s*\n+```(?:jsx|javascript|js|tsx|ts)?\n([\s\S]*?)```/g;
        while ((match = namedBlockRegex.exec(response)) !== null) {
            let filename = match[1].trim();
            let code = match[2].trim();
            if (filename && code.length > 50) {
                files[filename] = code;
                console.log('Method 3.5 - Extracted from named block:', filename, 'length:', code.length);
            }
        }
    }
    
    // Method 4: Extract React code without markdown
    if (Object.keys(files).length === 0) {
        const hasReactPatterns = response.includes('import React') && response.includes('const App');
        
        if (hasReactPatterns) {
            const importStart = response.indexOf('import React');
            if (importStart !== -1) {
                let code = response.substring(importStart);
                
                const exportPatterns = [
                    'export default App;',
                    'export default App',
                    'export { App as default };'
                ];
                
                let endIndex = -1;
                for (const pattern of exportPatterns) {
                    const idx = code.indexOf(pattern);
                    if (idx !== -1) {
                        endIndex = idx + pattern.length;
                        if (code[endIndex] === ';') endIndex++;
                        break;
                    }
                }
                
                if (endIndex !== -1) {
                    code = code.substring(0, endIndex);
                } else {
                    const textIndicators = [
                        '\n\nAceastă', '\n\nAceasta', '\n\nAplicația', '\n\nCodul',
                        '\n\n**Funcționalități', '\n\n## ', '\n\n### ', '\n\n---',
                        '\n\nNotă:', '\n\nNote:', '\n\n**Note'
                    ];
                    
                    for (const indicator of textIndicators) {
                        const idx = code.indexOf(indicator);
                        if (idx !== -1 && idx > 1000) {
                            code = code.substring(0, idx);
                            break;
                        }
                    }
                    
                    if (!code.includes('export default')) {
                        code = code.trim() + '\n\nexport default App;';
                    }
                }
                
                code = code.trim();
                
                const lines = code.split('\n');
                const cleanedLines = [];
                let foundCodeEnd = false;
                
                for (let i = lines.length - 1; i >= 0; i--) {
                    const line = lines[i].trim();
                    
                    if (!foundCodeEnd && line === '') continue;
                    
                    const looksLikeCode = 
                        line === '' || line.startsWith('//') || line.startsWith('/*') ||
                        line.startsWith('*') || line.startsWith('*/') ||
                        line.startsWith('import ') || line.startsWith('export ') ||
                        line.startsWith('const ') || line.startsWith('let ') ||
                        line.startsWith('var ') || line.startsWith('function ') ||
                        line.startsWith('return ') || line.startsWith('if ') ||
                        line.startsWith('}') || line.startsWith('{') ||
                        line.startsWith(');') || line.startsWith('});') ||
                        line.endsWith(';') || line.endsWith('{') ||
                        line.endsWith('}') || line.endsWith(',') ||
                        line.endsWith(')') || line.includes('className=') ||
                        line.includes('onClick=') || line.includes('useState') ||
                        line.includes('=>') || /^\s*</.test(line) ||
                        /^\s*\w+\s*\(/.test(line);
                    
                    if (looksLikeCode || foundCodeEnd) {
                        cleanedLines.unshift(lines[i]);
                        foundCodeEnd = true;
                    }
                }
                
                code = cleanedLines.join('\n').trim();
                
                const hasApp = code.includes('const App') || code.includes('function App');
                const hasReturn = code.includes('return');
                
                if (hasApp && hasReturn) {
                    if (!code.includes('export default')) {
                        code += '\n\nexport default App;';
                    }
                    files['App.jsx'] = code;
                    console.log('Method 4 - Extracted, final length:', code.length);
                }
            }
        }
    }
    
    // Method 5: Any large code block with React patterns
    if (Object.keys(files).length === 0) {
        const anyCodeBlock = /```[a-z]*\n([\s\S]*?)```/g;
        while ((match = anyCodeBlock.exec(response)) !== null) {
            const code = match[1].trim();
            const hasReactIndicators = (
                (code.includes('const App') || code.includes('function App') || code.includes('export default')) &&
                (code.includes('return') || code.includes('=>')) &&
                (code.includes('<') && code.includes('>'))
            );
            
            if (code.length > 100 && hasReactIndicators) {
                files['App.jsx'] = code;
                console.log('Method 5 - Extracted, length:', code.length);
                break;
            }
        }
    }
    
    // Method 6: Handle modified/updated code responses - look for any substantial JSX
    if (Object.keys(files).length === 0) {
        const anyCodeBlock = /```(?:jsx|javascript|js|tsx|ts|react)?\n([\s\S]*?)```/g;
        while ((match = anyCodeBlock.exec(response)) !== null) {
            const code = match[1].trim();
            
            // Check if it has React-like patterns even without explicit App component
            const hasImports = code.includes('import ');
            const hasJSX = /<[A-Z][a-zA-Z]*|<div|<span|<button|<header|<main|<section|<nav|<footer/i.test(code);
            const hasExport = code.includes('export ');
            
            if (code.length > 150 && hasJSX && (hasImports || hasExport)) {
                // Determine component name
                let filename = 'App.jsx';
                const componentMatch = code.match(/(?:export\s+(?:default\s+)?)?(?:const|function)\s+([A-Z][a-zA-Z0-9]*)\s*[=(:]/);
                if (componentMatch) {
                    filename = componentMatch[1] === 'App' ? 'App.jsx' : `${componentMatch[1]}.jsx`;
                }
                
                files[filename] = code;
                console.log('Method 6 - Extracted JSX component:', filename, 'length:', code.length);
                break;
            }
        }
    }
    
    // Validate and fix extracted code
    for (const [filename, code] of Object.entries(files)) {
        if (filename.endsWith('.jsx') || filename.endsWith('.js')) {
            console.log(`Validating ${filename}...`);
            let validation = validateCode(code, filename);
            
            if (!validation.valid) {
                console.log(`Validation failed: ${validation.error}`);
                const fixedCode = tryFixCode(code);
                validation = validateCode(fixedCode, filename);
                
                if (validation.valid) {
                    console.log(`Fix successful for ${filename}`);
                    files[filename] = fixedCode;
                }
            } else {
                console.log(`${filename} validation passed`);
            }
        }
    }
    
    console.log('Total files extracted:', Object.keys(files).length);
    return files;
};

/**
 * Get language for Monaco editor based on file extension
 */
export const getLanguage = (filename) => {
    if (!filename) return 'plaintext';
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap = {
        'js': 'javascript',
        'jsx': 'javascript',
        'ts': 'typescript',
        'tsx': 'typescript',
        'html': 'html',
        'css': 'css',
        'json': 'json',
        'md': 'markdown',
        'py': 'python',
        'sql': 'sql',
        'sh': 'shell',
        'bash': 'shell',
        'yml': 'yaml',
        'yaml': 'yaml',
        'xml': 'xml'
    };
    return languageMap[ext] || 'plaintext';
};
